import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { SearchOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styled from 'styled-components';

import { getUserManagementAvailableCommunities } from 'apis/UserAPI';
import Input from 'components/lib/Input';
import Select from 'components/lib/Select';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { useAppSelector } from 'store/hook';
import { SelectAdvancedFilterOptionType } from 'types/common';
import { CompanyType } from 'types/company';
import { useDebounce } from 'utils/hooks/useDebounce';

import CompanyGatedMultiSelectButton from '../../../CompanyGatedMultiSelectButton';
import { CMI_DIGEST_ADMIN_STATUS_OPTIONS } from '../../../constants';

const Container = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;
`;

const Filters = styled.div`
  display: flex;
  flex-flow: row;
  gap: 10px;
`;

const LeftFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;

const StyledSelect = styled(Select)`
  &&& .ant-select-selector {
    width: 170px;
    border-radius: 4px;
  }
`;

type Props = {
  isMultipleSelection: boolean;
  toggleMultipleSelection: () => void;
  companies: number[];
  setCompanies: (value: number[]) => void;
  community: number | null;
  setCommunity: (value: number | null) => void;
  status: string | null;
  setStatus: (value: string | null) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
};

const CmiDigestAdminToolbar = ({
  isMultipleSelection,
  toggleMultipleSelection,
  companies,
  setCompanies,
  community,
  setCommunity,
  status,
  setStatus,
  searchTerm,
  setSearchTerm,
}: Props) => {
  const { companies: availableCompanies } = useAppSelector((state) => state.global);

  const [communitySearch, setCommunitySearch] = useState('');
  const debouncedCommunitySearch = useDebounce(communitySearch);
  const [selectedCommunityOption, setSelectedCommunityOption] = useState<{
    label: string;
    value: number;
  } | null>(null);

  const singleCompanyId = companies.length === 1 ? companies[0] : undefined;

  const { data: communitiesResponse } = useQuery({
    queryKey: ['cmiDigestAdminCommunityFilterOptions', debouncedCommunitySearch, singleCompanyId],
    queryFn: () =>
      getUserManagementAvailableCommunities({
        search: debouncedCommunitySearch,
        company_id: singleCompanyId,
        page_size: 200,
      }),
  });

  const fetchedCommunityOptions = (communitiesResponse?.results ?? []).map((community: any) => ({
    label: community.comunity_name,
    value: community.id,
  }));

  const communityOptions = [
    { label: 'Any Community', value: null },
    ...(selectedCommunityOption &&
    !fetchedCommunityOptions.some((option: { value: number }) => option.value === selectedCommunityOption.value)
      ? [selectedCommunityOption]
      : []),
    ...fetchedCommunityOptions,
  ];

  const handleCommunityChange = (value: number | null, option: any) => {
    setCommunity(value);
    setSelectedCommunityOption(value === null ? null : { label: option.label, value });
    setCommunitySearch('');
  };

  const companyOptions = (availableCompanies || []).map((company: CompanyType) => ({
    label: company.name,
    value: company.id,
  }));

  const handleSelectCompany = (values: SelectAdvancedFilterOptionType[]) => {
    setCompanies((values ?? []).map((item: SelectAdvancedFilterOptionType) => Number(item.value)));
  };

  return (
    <Container>
      <LeftFilters>
        <CompanyGatedMultiSelectButton
          isMultipleSelection={isMultipleSelection}
          toggleMultipleSelection={toggleMultipleSelection}
          disabled={companies.length == 0}
          dataTestId="cmi-digest-admin-multi-select-toggle"
        />
        <SelectAdvancedFilter
          className={classNames({ active: companies.length > 0 })}
          placeholder={'Company'}
          options={companyOptions}
          defaultValue={companies}
          onSelect={handleSelectCompany}
          dataTestId="cmi-digest-admin-company-filter"
        />
      </LeftFilters>
      <Filters>
        <StyledSelect
          showSearch
          filterOption={false}
          options={communityOptions}
          value={community}
          onChange={handleCommunityChange}
          onSearch={setCommunitySearch}
          popupMatchSelectWidth={false}
          dataTestId="cmi-digest-admin-community-filter"
          suffixIcon={<SearchOutlined />}
          showArrow
        />
        <StyledSelect
          options={CMI_DIGEST_ADMIN_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          dataTestId="cmi-digest-admin-status-filter"
        />
        <Input
          value={searchTerm}
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
          onChange={(e: any) => setSearchTerm(e.target.value)}
          placeholder="Search users"
          data-test-id="cmi-digest-admin-search-input"
        />
      </Filters>
    </Container>
  );
};

export default CmiDigestAdminToolbar;
